import { ChevronRight } from 'lucide-react';
import { Collapsible } from '@/components/ui/collapsible';
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	useSidebar,
} from '@/components/ui/sidebar';
import { useNavigate } from 'react-router-dom';
import {
	SidebarButtons,
	SidebarItem,
	SidebarList,
} from '@/components/everridge/sidebar/types';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
	data: SidebarList;
	type?: 'Header' | 'Footer' | 'Main' | 'Settings';
	bottomBorder?: boolean;
	activeBtn?: SidebarButtons;
	setActiveBtn?: (type: SidebarButtons) => void;
};

const ESidebarContentCreator: FC<Props> = ({
	data,
	type,
	bottomBorder,
	activeBtn,
	setActiveBtn,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { toggleSidebar, isMobile } = useSidebar();

	const actionHandler = (item: SidebarItem) => {
		if (type === 'Header') {
			return toggleSidebar();
		}

		if (type === 'Footer') {
			setActiveBtn?.(SidebarButtons.Dashboard);
			return navigate('/');
		}

		setActiveBtn?.(item.type);
		if (isMobile) toggleSidebar();
		return navigate(item.url);
	};

	if (!data) return null;

	return (
		<SidebarGroup
			className={`${
				bottomBorder ? 'border-b border-primary-color-800' : ''
			}`}>
			{data.title !== 'Header' && data.title !== 'Footer' && (
				<SidebarGroupLabel className='text-primary-color-400'>
					{t(`${data.title}`)}
				</SidebarGroupLabel>
			)}
			<SidebarMenu>
				{data.items?.map((item) => (
					<Collapsible
						key={item.title}
						asChild
						className='group/collapsible'>
						<SidebarMenuItem>
							<SidebarMenuButton
								tooltip={t(item.title)}
								onClick={() => actionHandler(item)}
								isActive={item.type === activeBtn}>
								{item.type === activeBtn
									? item.activeIcon && (
											<item.activeIcon className='fill-primary-color-10 stroke-primary-color-10' />
									  )
									: item.icon && <item.icon />}
								<span
									className={
										'text-sm text-primary-color-10 font-medium group-data-[collapsible=icon]:hidden select-none'
									}>
									{t(item.title)}
								</span>
								{item.items &&
									data.title !== 'Header' &&
									data.title !== 'Footer' && (
										<ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
									)}
							</SidebarMenuButton>
						</SidebarMenuItem>
					</Collapsible>
				))}
			</SidebarMenu>
		</SidebarGroup>
	);
};

export default ESidebarContentCreator;
